import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../api/axios';
import { T, PageHeader, Section, Field, Alert } from '../components/UI';
import { useAuth } from '../context/AuthContext';

const SERVICES = [
  'Outside Only','Inside & Out','Premium Wash','Full Detail','Signature Detail','The Works',
  'Ceramic Coating','Interior Protection Pack','Windows Tinting','Paintless Dent Removal',
  'Stage 3 Paint Correction','Buff and Polish','Head Light Restoration',
  'Leather Clean/Seats Steam Clean','Dog Hair Removal','Bull Bar Polish'
];

const STATUS_LABEL = { pending:'Pending', ready:'Ready', collected:'Collected', paid:'Paid' };

const TODAY = new Date(new Date().getTime()+8*60*60*1000).toISOString().split('T')[0];

const EMPTY = {
  date: TODAY, rego:'', services:[], price:'', notes:'',
  customerName:'', phone:'', customerEmail:'', pickupTime:'',
};

export default function WalkInForm() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const location = useLocation();
  const booking  = !id ? location.state?.booking : null;   // coming from an online booking on the sheet: details are filled in for the customer
  const editMode = !!id;
  const { user }  = useAuth();
  const [form,    setForm]    = useState(() => booking ? {
    ...EMPTY, date: location.state?.date || TODAY, rego: booking.rego || '', services: (booking.services || []).filter(s => SERVICES.includes(s)),
    customerName: booking.name || '', phone: booking.phone || '', customerEmail: booking.email || '',
    notes: [`Online booking ${booking.reference || ''} at ${booking.time || ''}`.trim(), booking.message].filter(Boolean).join(' — '),
  } : EMPTY);
  const [loading, setLoading] = useState(editMode);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const [history,        setHistory]        = useState([]);
  const [historyChecking,setHistoryChecking]= useState(false);
  const [showHistory,    setShowHistory]    = useState(false);
  const historyTimer = useRef(null);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  // Edit mode: load the saved walk-in into the form
  useEffect(() => {
    if (!editMode) return;
    api.get(`/walkins/${id}`)
      .then(({ data }) => {
        const w = data.walkIn;
        setForm({
          date: w.date, rego: w.rego || '', services: w.services?.length ? w.services : (w.service ? [w.service] : []),
          price: w.price ? String(w.price) : '', notes: w.notes || '', customerName: w.customerName || '', phone: w.phone || '', customerEmail: w.customerEmail || '', pickupTime: w.pickupTime || '',
        });
      })
      .catch(err => setError(err.response?.data?.message || 'Could not load this walk-in'))
      .finally(() => setLoading(false));
  }, [id, editMode]);

  const handleRegoChange = (val) => {
    const upper = val.toUpperCase();
    set('rego', upper);
    setHistory([]); setShowHistory(false);
    clearTimeout(historyTimer.current);
    if (upper.length >= 3) {
      historyTimer.current = setTimeout(async () => {
        setHistoryChecking(true);
        try {
          const { data } = await api.get('/walkins/history', { params: { rego: upper } });
          setHistory(data.history || []);
        } catch {}
        finally { setHistoryChecking(false); }
      }, 600);
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.services.length) errs.service   = true;
    if (!form.customerName) errs.customerName = true;
    if (!form.phone.trim()) errs.phone = true;
    if (form.customerEmail.trim() && !/^\S+@\S+\.\S+$/.test(form.customerEmail.trim())) errs.customerEmail = 'Enter a valid email address';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true); setError(null);
    try {
      const body = { ...form, price: parseFloat(form.price)||0, ...(editMode ? {} : { location: user?.selectedLocation || '', ...(booking ? { onlineBookingId: booking.id } : {}) }) };
      if (editMode) await api.patch(`/walkins/${id}`, body);
      else await api.post('/walkins', body);
      navigate('/walkins', { state: { date: form.date } });   // back to the same day's sheet
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/>
    </div>
  );

  return (
    <div style={T.page}>
      <PageHeader title={editMode ? 'Edit Walk-in' : 'New Walk-in'} onBack={()=>navigate('/walkins', { state: { date: form.date } })}/>
      <form onSubmit={handleSubmit} noValidate style={{ maxWidth:'580px', margin:'0 auto', padding:'20px 16px 100px' }}>
        {error && <Alert type="error" style={{ marginBottom:'16px' }}>{error}</Alert>}

        <Section title="Walk-in Details" accent>
          <Field label="Rego">
            <input value={form.rego} onChange={e=>handleRegoChange(e.target.value)}
              style={{ ...T.input, fontFamily:'JetBrains Mono,monospace' }} placeholder="e.g. 1ABC234"/>
            {historyChecking && <p style={{ fontSize:'11px', color:'var(--muted)', marginTop:'4px' }}>Checking history...</p>}
            {!historyChecking && history.length > 0 && (
              <div style={{ marginTop:'8px' }}>
                <button type="button" onClick={()=>setShowHistory(h=>!h)}
                  style={{ ...T.btnSecondary, width:'100%', justifyContent:'space-between', fontSize:'12px', borderColor:'var(--amber)', color:'var(--amber)' }}>
                  <span><i className="ti ti-history" style={{ marginRight:'6px' }} aria-hidden="true"/>{history.length} previous visit{history.length>1?'s':''} for this rego</span>
                  <i className={`ti ti-chevron-${showHistory?'up':'down'}`} aria-hidden="true"/>
                </button>
                {showHistory && (
                  <div style={{ border:'1px solid var(--border)', borderTop:'none', borderRadius:'0 0 var(--radius) var(--radius)', overflow:'auto', maxHeight:'220px' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'420px' }}>
                      <thead>
                        <tr>
                          {['Date','Service','Price','Status'].map(h=><th key={h} style={T.th}>{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {history.filter(w => w._id !== id).map((w,i)=>(
                          <tr key={w._id} style={{ background:i%2===0?'var(--surface)':'var(--bg)' }}>
                            <td style={{ ...T.td, fontFamily:'JetBrains Mono,monospace', color:'var(--muted)' }}>{new Date(w.createdAt).toLocaleDateString('en-AU')}</td>
                            <td style={T.td}>{w.service}</td>
                            <td style={{ ...T.td, fontFamily:'JetBrains Mono,monospace', color:'var(--green)', fontWeight:'500' }}>${(w.price||0).toFixed(2)}</td>
                            <td style={T.td}>{STATUS_LABEL[w.status]||w.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </Field>
          <Field label={`Services * ${form.services.length ? `(${form.services.length} selected)` : ''}`} error={fieldErrors.service?'Choose at least one service':null}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(210px, 1fr))', gap:'8px' }}>
              {SERVICES.map(s => {
                const on = form.services.includes(s);
                return (
                  <label key={s} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'var(--radius)', cursor:'pointer', fontSize:'13px', userSelect:'none',
                    border:`1px solid ${on ? 'var(--blue)' : fieldErrors.service ? 'var(--red)' : 'var(--border-md)'}`, background: on ? 'var(--blue-light)' : 'var(--surface)', color: on ? 'var(--blue)' : 'var(--text)', fontWeight: on ? '500' : '400' }}>
                    <input type="checkbox" checked={on} onChange={() => { set('services', on ? form.services.filter(x => x !== s) : [...form.services, s]); setFieldErrors(fe => ({ ...fe, service:false })); }}
                      style={{ width:'17px', height:'17px', accentColor:'var(--blue)', flexShrink:0 }}/>
                    {s}
                  </label>
                );
              })}
            </div>
          </Field>
          <Field label="Total price ($)">
            <div style={{ position:'relative' }}>
              <span style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', color:'var(--muted)', fontSize:'13px' }}>$</span>
              <input type="number" value={form.price} min="0" step="0.01"
                onChange={e=>set('price',e.target.value)}
                style={{ ...T.input, paddingLeft:'24px' }} placeholder="0.00"/>
            </div>
          </Field>
          <Field label="Notes / Extras">
            <textarea value={form.notes} onChange={e=>set('notes',e.target.value)}
              rows={3} style={{ ...T.input, resize:'vertical' }} placeholder="Any extra notes or requests..."/>
          </Field>
          <Field label="Customer Name *" error={fieldErrors.customerName?'Required':null}>
            <input value={form.customerName} onChange={e=>{set('customerName',e.target.value); setFieldErrors(fe=>({...fe,customerName:false}));}}
              style={{ ...T.input, border:`1px solid ${fieldErrors.customerName?'var(--red)':'var(--border-md)'}` }} placeholder="Full name"/>
          </Field>
          <Field label="Phone *" error={fieldErrors.phone?'Required':null}>
            <input value={form.phone} onChange={e=>{set('phone',e.target.value); setFieldErrors(fe=>({...fe,phone:false}));}}
              style={{ ...T.input, border:`1px solid ${fieldErrors.phone?'var(--red)':'var(--border-md)'}` }} placeholder="Mobile number" type="tel"/>
          </Field>
          <Field label="Email (optional)" hint="Used to send the customer their tax invoice." error={fieldErrors.customerEmail || null}>
            <input value={form.customerEmail} onChange={e=>{set('customerEmail',e.target.value); setFieldErrors(fe=>({...fe,customerEmail:false}));}}
              style={{ ...T.input, border:`1px solid ${fieldErrors.customerEmail?'var(--red)':'var(--border-md)'}` }} placeholder="name@example.com" type="email" autoCapitalize="none"/>
          </Field>
          <Field label="Pickup Time">
            <input type="time" value={form.pickupTime} onChange={e=>set('pickupTime',e.target.value)} style={T.input}/>
          </Field>
        </Section>

        <div style={{ display:'flex', gap:'8px', paddingTop:'8px' }}>
          <button type="button" onClick={()=>navigate('/walkins', { state: { date: form.date } })} style={{ ...T.btnSecondary, flex:1 }}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...T.btnPrimary, flex:2, justifyContent:'center', opacity:saving?0.6:1 }}>
            <i className="ti ti-check" aria-hidden="true"/> {saving?'Saving...':editMode?'Save Changes':'Save Walk-in'}
          </button>
        </div>
      </form>
    </div>
  );
}
